import React, { useState, useEffect } from 'react';
import { useBaseUrl } from '../../../utils/utils';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faFile, faFileAlt, faFilePdf, faFileImage, faFileWord, faFileExcel,
    faDownload, faUpload, faEye, faTrash, faEdit, faShare, faLock,
    faUnlock, faFolder, faFolderOpen, faSearch, faFilter, faSort,
    faCalendarAlt, faUser, faShieldAlt, faCheckCircle, faTimesCircle,
    faCog
} from '@fortawesome/free-solid-svg-icons';

interface CollaborationDocument {
    document_id: string;
    collaboration_id: string;
    title: string;
    description: string;
    file_type: string;
    file_size: number;
    file_path: string;
    origin: string;
    uploaded_by: {
        id: number;
        name: string;
        type: string;
    };
    upload_date: string;
    access_level: 'public' | 'restricted' | 'private';
    recipients: Array<{
        id: number;
        name: string;
        type: string;
        access_granted: boolean;
        download_requested: boolean;
        download_approved: boolean;
    }>;
    tags: string[];
    version: string;
    checksum: string;
    status: 'active' | 'archived' | 'deleted';
}

interface Props {
    collaborationId?: string;
    onClose?: () => void;
}

const DocumentManagement: React.FC<Props> = ({ collaborationId, onClose }) => {
    const baseUrl = useBaseUrl();
    const [documents, setDocuments] = useState<CollaborationDocument[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<CollaborationDocument | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState<string>('all');
    const [sortBy, setSortBy] = useState<string>('upload_date');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [uploadForm, setUploadForm] = useState({
        title: '',
        description: '',
        access_level: 'restricted',
        recipients: [] as number[],
        tags: ''
    });

    useEffect(() => {
        if (collaborationId) {
            fetchDocuments();
        }
    }, [collaborationId, baseUrl]);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${baseUrl}collaborations/documents/`, {
                params: { collaboration_id: collaborationId }
            });
            if (response.data.status === 'success') {
                setDocuments(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to fetch documents'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (file.size > 50 * 1024 * 1024) { // 50MB limit
            Swal.fire({
                icon: 'error',
                title: 'File Too Large',
                text: 'File size must be less than 50MB'
            });
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('collaboration_id', collaborationId || '');
        formData.append('title', uploadForm.title);
        formData.append('description', uploadForm.description);
        formData.append('access_level', uploadForm.access_level);
        formData.append('recipients', JSON.stringify(uploadForm.recipients));
        formData.append('tags', uploadForm.tags);

        try {
            const response = await axios.post(`${baseUrl}collaborations/documents/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Document Uploaded',
                    text: 'Document has been successfully uploaded'
                });
                setShowUploadModal(false);
                setUploadForm({
                    title: '',
                    description: '',
                    access_level: 'restricted',
                    recipients: [],
                    tags: ''
                });
                fetchDocuments();
            }
        } catch (error) {
            console.error('Error uploading document:', error);
            Swal.fire({
                icon: 'error',
                title: 'Upload Failed',
                text: 'Failed to upload document. Please try again.'
            });
        } finally {
            setUploading(false);
        }
    };

    const requestDownload = async (documentId: string) => {
        try {
            const response = await axios.post(`${baseUrl}collaborations/documents/${documentId}/request-download/`);
            if (response.data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Download Requested',
                    text: 'Your download request has been submitted for approval'
                });
                fetchDocuments();
            }
        } catch (error) {
            console.error('Error requesting download:', error);
            Swal.fire({
                icon: 'error',
                title: 'Request Failed',
                text: 'Failed to request download. Please try again.'
            });
        }
    };

    const approveDownload = async (documentId: string, recipientId: number) => {
        try {
            const response = await axios.post(`${baseUrl}collaborations/documents/${documentId}/approve-download/`, {
                recipient_id: recipientId
            });
            if (response.data.status === 'success') {
                Swal.fire({
                    icon: 'success',
                    title: 'Download Approved',
                    text: 'Download access has been granted'
                });
                fetchDocuments();
            }
        } catch (error) {
            console.error('Error approving download:', error);
            Swal.fire({
                icon: 'error',
                title: 'Approval Failed',
                text: 'Failed to approve download. Please try again.'
            });
        }
    };

    const deleteDocument = async (documentId: string) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Delete Document',
            text: 'Are you sure you want to delete this document? This action cannot be undone.',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc3545'
        });

        if (result.isConfirmed) {
            try {
                const response = await axios.delete(`${baseUrl}collaborations/documents/${documentId}/`);
                if (response.data.status === 'success') {
                    Swal.fire({
                        icon: 'success',
                        title: 'Document Deleted',
                        text: 'Document has been successfully deleted'
                    });
                    fetchDocuments();
                }
            } catch (error) {
                console.error('Error deleting document:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Deletion Failed',
                    text: 'Failed to delete document. Please try again.'
                });
            }
        }
    };

    const getFileIcon = (fileType: string) => {
        const iconMap: { [key: string]: any } = {
            'pdf': faFilePdf,
            'doc': faFileWord,
            'docx': faFileWord,
            'xls': faFileExcel,
            'xlsx': faFileExcel,
            'jpg': faFileImage,
            'jpeg': faFileImage,
            'png': faFileImage,
            'gif': faFileImage
        };
        return iconMap[fileType.toLowerCase()] || faFile;
    };

    const getFileTypeColor = (fileType: string) => {
        const colorMap: { [key: string]: string } = {
            'pdf': 'text-danger',
            'doc': 'text-primary',
            'docx': 'text-primary',
            'xls': 'text-success',
            'xlsx': 'text-success',
            'jpg': 'text-info',
            'jpeg': 'text-info',
            'png': 'text-info',
            'gif': 'text-info'
        };
        return colorMap[fileType.toLowerCase()] || 'text-secondary';
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const filteredAndSortedDocuments = documents
        .filter(doc => {
            const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                doc.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                doc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
            const matchesFilter = filterType === 'all' || doc.access_level === filterType;
            return matchesSearch && matchesFilter;
        })
        .sort((a, b) => {
            let aValue: any = a[sortBy as keyof CollaborationDocument];
            let bValue: any = b[sortBy as keyof CollaborationDocument];
            
            if (sortBy === 'upload_date') {
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
            }
            
            if (sortOrder === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h3 className="mb-1">
                        <FontAwesomeIcon icon={faFolder} className="me-3 text-primary" />
                        Document Management
                    </h3>
                    <p className="text-muted mb-0">Secure file sharing and management for collaboration</p>
                </div>
                <div>
                    <button 
                        className="btn btn-primary me-2"
                        onClick={() => setShowUploadModal(true)}
                    >
                        <FontAwesomeIcon icon={faUpload} className="me-2" />
                        Upload Document
                    </button>
                    {onClose && (
                        <button className="btn btn-outline-secondary" onClick={onClose}>
                            Close
                        </button>
                    )}
                </div>
            </div>

            {/* Search and Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <FontAwesomeIcon icon={faSearch} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search documents..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={filterType}
                                onChange={(e) => setFilterType(e.target.value)}
                            >
                                <option value="all">All Access Levels</option>
                                <option value="public">Public</option>
                                <option value="restricted">Restricted</option>
                                <option value="private">Private</option>
                            </select>
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="upload_date">Upload Date</option>
                                <option value="title">Title</option>
                                <option value="file_size">File Size</option>
                                <option value="access_level">Access Level</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <button
                                className="btn btn-outline-secondary w-100"
                                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                            >
                                <FontAwesomeIcon icon={faSort} className="me-2" />
                                {sortOrder === 'asc' ? 'Asc' : 'Desc'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Documents Grid */}
            <div className="row">
                {filteredAndSortedDocuments.map((doc) => (
                    <div key={doc.document_id} className="col-lg-4 col-md-6 mb-4">
                        <div className="card h-100">
                            <div className="card-header d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon 
                                        icon={getFileIcon(doc.file_type)} 
                                        className={`me-2 ${getFileTypeColor(doc.file_type)}`}
                                        size="lg"
                                    />
                                    <span className="badge bg-secondary">{doc.file_type.toUpperCase()}</span>
                                </div>
                                <div className="dropdown">
                                    <button className="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                                        <FontAwesomeIcon icon={faCog} />
                                    </button>
                                    <ul className="dropdown-menu">
                                        <li>
                                            <button 
                                                className="dropdown-item"
                                                onClick={() => {
                                                    setSelectedDocument(doc);
                                                    setShowDocumentModal(true);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faEye} className="me-2" />
                                                View Details
                                            </button>
                                        </li>
                                        <li>
                                            <button 
                                                className="dropdown-item"
                                                onClick={() => requestDownload(doc.document_id)}
                                            >
                                                <FontAwesomeIcon icon={faDownload} className="me-2" />
                                                Request Download
                                            </button>
                                        </li>
                                        <li>
                                            <button 
                                                className="dropdown-item text-danger"
                                                onClick={() => deleteDocument(doc.document_id)}
                                            >
                                                <FontAwesomeIcon icon={faTrash} className="me-2" />
                                                Delete
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="card-body">
                                <h6 className="card-title">{doc.title}</h6>
                                <p className="card-text text-muted small">{doc.description}</p>
                                <div className="mb-2">
                                    {doc.tags.map((tag, index) => (
                                        <span key={index} className="badge bg-light text-dark me-1">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                    <small className="text-muted">
                                        <FontAwesomeIcon icon={faUser} className="me-1" />
                                        {doc.uploaded_by.name}
                                    </small>
                                    <small className="text-muted">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                        {new Date(doc.upload_date).toLocaleDateString()}
                                    </small>
                                </div>
                            </div>
                            <div className="card-footer">
                                <div className="d-flex justify-content-between align-items-center">
                                    <small className="text-muted">
                                        {formatFileSize(doc.file_size)}
                                    </small>
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon 
                                            icon={doc.access_level === 'public' ? faUnlock : faLock} 
                                            className={`me-1 ${doc.access_level === 'public' ? 'text-success' : 'text-warning'}`}
                                        />
                                        <small className="text-muted">{doc.access_level}</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {filteredAndSortedDocuments.length === 0 && (
                <div className="text-center py-5">
                    <FontAwesomeIcon icon={faFolder} size="3x" className="text-muted mb-3" />
                    <h5>No Documents Found</h5>
                    <p className="text-muted">Upload your first document to get started</p>
                </div>
            )}

            {/* Upload Modal */}
            {showUploadModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                                    Upload Document
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close"
                                    onClick={() => setShowUploadModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Document Title *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={uploadForm.title}
                                        onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                                        placeholder="Enter document title"
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        value={uploadForm.description}
                                        onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                                        placeholder="Enter document description"
                                    />
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Access Level</label>
                                            <select
                                                className="form-select"
                                                value={uploadForm.access_level}
                                                onChange={(e) => setUploadForm({...uploadForm, access_level: e.target.value as any})}
                                            >
                                                <option value="public">Public</option>
                                                <option value="restricted">Restricted</option>
                                                <option value="private">Private</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Tags</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={uploadForm.tags}
                                                onChange={(e) => setUploadForm({...uploadForm, tags: e.target.value})}
                                                placeholder="Enter tags separated by commas"
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Select File *</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        onChange={handleFileUpload}
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                                        required
                                    />
                                    <small className="text-muted">
                                        Supported formats: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, GIF (Max: 50MB)
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => setShowUploadModal(false)}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    disabled={uploading || !uploadForm.title}
                                >
                                    {uploading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" />
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faUpload} className="me-2" />
                                            Upload
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Document Details Modal */}
            {showDocumentModal && selectedDocument && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <FontAwesomeIcon icon={faFile} className="me-2" />
                                    Document Details
                                </h5>
                                <button 
                                    type="button" 
                                    className="btn-close"
                                    onClick={() => setShowDocumentModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-8">
                                        <h6>{selectedDocument.title}</h6>
                                        <p className="text-muted">{selectedDocument.description}</p>
                                        <div className="mb-3">
                                            <strong>Tags:</strong>
                                            {selectedDocument.tags.map((tag, index) => (
                                                <span key={index} className="badge bg-light text-dark me-1">
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <p><strong>File Type:</strong> {selectedDocument.file_type.toUpperCase()}</p>
                                                <p><strong>File Size:</strong> {formatFileSize(selectedDocument.file_size)}</p>
                                                <p><strong>Version:</strong> {selectedDocument.version}</p>
                                            </div>
                                            <div className="col-md-6">
                                                <p><strong>Uploaded By:</strong> {selectedDocument.uploaded_by.name}</p>
                                                <p><strong>Upload Date:</strong> {new Date(selectedDocument.upload_date).toLocaleDateString()}</p>
                                                <p><strong>Access Level:</strong> {selectedDocument.access_level}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="card">
                                            <div className="card-header">
                                                <h6 className="mb-0">Access Control</h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="mb-3">
                                                    <strong>Recipients:</strong>
                                                    {selectedDocument.recipients.map((recipient, index) => (
                                                        <div key={index} className="d-flex justify-content-between align-items-center mt-2">
                                                            <span>{recipient.name}</span>
                                                            <div>
                                                                {recipient.download_requested && !recipient.download_approved && (
                                                                    <button
                                                                        className="btn btn-sm btn-success"
                                                                        onClick={() => approveDownload(selectedDocument.document_id, recipient.id)}
                                                                    >
                                                                        <FontAwesomeIcon icon={faCheckCircle} />
                                                                    </button>
                                                                )}
                                                                {recipient.download_approved && (
                                                                    <span className="badge bg-success">
                                                                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                                                        Approved
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => setShowDocumentModal(false)}
                                >
                                    Close
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={() => requestDownload(selectedDocument.document_id)}
                                >
                                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                                    Request Download
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentManagement;
